import { createSelector } from 'reselect';

import type CarrierAccount from '~/types/CarrierAccount';
import type ProviderAccount from '~/types/ProviderAccount';
import type ServiceLevel from '~/types/ServiceLevel';

import { type ProvidersState } from '~/reducers/providers';
import {
  type MappedProviderAccount,
  type ObjectIdProviderAccountMap,
} from '~/types/ProviderAccount';
import { getServiceLevelAccountByToken } from '~/types/ServiceLevel';
import {
  requestableHasFailed,
  requestableHasSucceeded,
  requestableIsLoading,
} from '~/utils/fetchable';

import { type ApplicationState } from '../store';
import { getUserDefaultReturnServiceLevel } from './user.selectors';

const getProvidersStore = (state: ApplicationState): ProvidersState => state.providers;

const getCarrierAccountFromProps = (_: unknown, props: { carrierAccount: CarrierAccount }) =>
  props.carrierAccount;

export const getServiceLevelsByAccountRequest = createSelector(
  getProvidersStore,
  (providersStore) => providersStore.serviceLevelsByAccountRequest,
);

export const getMappedServiceLevelsByAccount = createSelector(
  getProvidersStore,
  (providersStore) => providersStore.mappedServiceLevelsByAccount,
);

export const getMappedServiceLevelsByToken = createSelector(
  getProvidersStore,
  (providersStore) => providersStore.mappedServiceLevelsByToken,
);

// get selected Carrier Account with Service Levels
export const getCarrierWithServiceLevelsByAccount = createSelector(
  getProvidersStore,
  getCarrierAccountFromProps,
  (providersStore, carrierProps: CarrierAccount) => {
    const mappedCarriers: MappedProviderAccount = providersStore.mappedServiceLevelsByAccount;
    const targetCarrier: ProviderAccount[] = mappedCarriers[carrierProps?.carrier?.providerToken];
    if (!targetCarrier) return;
    return targetCarrier.find((carrier) => carrier?.objectId === carrierProps?.objectId);
  },
);

export const getAllServiceLevelsMapByAccount = createSelector(
  getProvidersStore,
  (providersStore): ObjectIdProviderAccountMap => {
    const allServiceLevelsByAccount: ObjectIdProviderAccountMap = {};
    Object.values(providersStore.mappedServiceLevelsByAccount).forEach((providerAccounts) => {
      providerAccounts.forEach((providerAccount) => {
        const serviceLevelsByToken: Record<string, ServiceLevel> = {};
        providerAccount.allServiceLevels.forEach((serviceLevel) => {
          serviceLevelsByToken[serviceLevel.token] = serviceLevel;
        });
        allServiceLevelsByAccount[providerAccount.objectId] = {
          ...providerAccount,
          serviceLevelsByToken,
        };
      });
    });

    return allServiceLevelsByAccount;
  },
);

// returns true if the serviceLevelsByAccountRequest has been made and has not yet failed
// (e.g. to determine whether to make a new request)
export const getIsServiceLevelsRequestInitiated = createSelector(
  getProvidersStore,
  (providersStore): boolean => {
    const request = providersStore.serviceLevelsByAccountRequest;
    return requestableIsLoading(request) || requestableHasSucceeded(request);
  },
);

// returns true if the serviceLevelsByAccountRequest has finished, regardless of whether it
// succeeded or failed (e.g. for determining whether to show a loading spinner)
export const getIsServiceLevelsRequestComplete = createSelector(
  getProvidersStore,
  (providersStore): boolean => {
    const request = providersStore.serviceLevelsByAccountRequest;
    return requestableHasFailed(request) || requestableHasSucceeded(request);
  },
);

export const getUserDefaultReturnRate = createSelector(
  getMappedServiceLevelsByToken,
  getUserDefaultReturnServiceLevel,
  (mappedServiceLevelsByToken, userDefaultReturnServiceLevel) =>
    getServiceLevelAccountByToken({
      accountId: undefined,
      mappedServiceLevels: mappedServiceLevelsByToken,
      token: userDefaultReturnServiceLevel?.token,
    }),
);
